// Project chat (RF-UI-11, RF-CHAT-01..04, Task 12): "Ask" is the assistant panel's own conversation component (no
// second chat UI, spec §3.8) — full AI when configured, rules mode otherwise. The typed commands (create task,
// guidance) are unchanged: they go through the same policies and idempotency as the interface and confirm what was
// created with links, in their own conversation (a different session/backend than the assistant panel, R16).
// Stopping a reply only stops waiting for it; it never cancels tasks.
import { useEffect, useRef, useState, type FormEvent } from "react";
import type { Board, TaskCard } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { getProjectSession, listProjectSessions, sendProjectMessage } from "../../api/endpoints-wave2.ts";
import type { AssistantCommand } from "../../api/types-wave2.ts";
import { newId, readLocal, writeLocal } from "../../lib/storage.ts";
import { IconButton } from "../../components/Button.tsx";
import { SelectField, TextAreaField, TextField } from "../../components/Field.tsx";
import { MessageIcon, SendIcon, XIcon } from "../../components/icons.tsx";
import { AssistantConversation } from "../assistant/AssistantConversation.tsx";
import { ChatEntryView, projectRefLink, type ChatEntry } from "./ChatParts.tsx";

type Mode = "ask" | "task" | "guidance";

function allTasks(board: Board | undefined): TaskCard[] {
  if (!board) return [];
  return [...board.columns.flatMap((c) => c.tasks), ...board.side_lane.flatMap((s) => s.tasks)];
}

export function ProjectChat({ projectId, board }: { projectId: string; board: Board | undefined }) {
  const { t } = useI18n();
  const storageKey = `chatSession.${projectId}`;
  const [sessionId, setSessionId] = useState<string | null>(() => readLocal<string | null>(storageKey, null));
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [historyState, setHistoryState] = useState<"loading" | "ready" | "failed">("loading");
  const [mode, setMode] = useState<Mode>("ask");
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [criteria, setCriteria] = useState("");
  const [taskId, setTaskId] = useState("");
  const [local, setLocal] = useState<string | null>(null);
  const inflight = useRef<AbortController | null>(null);
  const [busy, setBusy] = useState(false);
  const listRef = useRef<HTMLOListElement>(null);
  const tasks = allTasks(board).filter((x) => x.status !== "done" && x.status !== "cancelled");
  const taskLabel = (id: string) => {
    const found = allTasks(board).find((x) => x.id === id);
    return found ? `#${found.number} ${found.title}` : null;
  };

  // Resume the latest conversation of this project (text only: refs are not stored).
  useEffect(() => {
    const controller = new AbortController();
    setHistoryState("loading");
    (async () => {
      try {
        let id = readLocal<string | null>(storageKey, null);
        if (!id) id = (await listProjectSessions(projectId, controller.signal)).sessions[0]?.id ?? null;
        if (!id) {
          setHistoryState("ready");
          return;
        }
        const history = await getProjectSession(projectId, id, controller.signal);
        if (controller.signal.aborted) return;
        setSessionId(history.session_id);
        setEntries(history.items.slice(-30).map((m) => ({ kind: "history", id: m.id, role: m.author_role, text: m.content, at: m.created_at })));
        setHistoryState("ready");
      } catch {
        if (controller.signal.aborted) return;
        // A stale stored session (deleted, other project) simply starts a new conversation.
        setSessionId(null);
        setHistoryState("failed");
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => () => inflight.current?.abort(), []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    // Follow new messages only when the reader is already at the bottom (RF-UI-15).
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) el.scrollTop = el.scrollHeight;
  }, [entries]);

  const send = async (content: string, command: AssistantCommand | null, key: string, echo: boolean) => {
    const pendingId = newId();
    setEntries((list) => [
      ...list.filter((e) => e.kind !== "cancelled"),
      ...(echo ? [{ kind: "user" as const, id: newId(), text: content, command, at: new Date().toISOString() }] : []),
      { kind: "pending", id: pendingId, command },
    ]);
    const controller = new AbortController();
    inflight.current = controller;
    setBusy(true);
    const retry = () => void send(content, command, key, false);
    try {
      const res = await sendProjectMessage(projectId, { content, ...(sessionId ? { session_id: sessionId } : {}), ...(command ? { command } : {}) }, key, controller.signal);
      setSessionId(res.session_id);
      writeLocal(storageKey, res.session_id);
      setEntries((list) => list.map((e) => (e.id === pendingId ? { kind: "reply", id: res.reply.id, reply: res.reply, signals: res.engine_signals ?? [] } : e)));
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") {
        setEntries((list) => list.map((x) => (x.id === pendingId ? { kind: "cancelled", id: pendingId, command, retry } : x)));
      } else {
        setEntries((list) => list.map((x) => (x.id === pendingId ? { kind: "error", id: pendingId, error: e, retry } : x)));
      }
    } finally {
      if (inflight.current === controller) inflight.current = null;
      setBusy(false);
    }
  };

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    const body = text.trim();
    if (mode === "task") {
      const taskTitle = (title.trim() || body.split(/\r?\n/)[0] || "").slice(0, 300);
      if (!body || !taskTitle) {
        setLocal(t("chat.taskRequired"));
        return;
      }
      const acceptance = criteria
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      setLocal(null);
      setText("");
      setTitle("");
      setCriteria("");
      void send(body, { type: "create_task", task: { title: taskTitle, acceptance_criteria: acceptance } }, newId(), true);
      return;
    }
    if (!taskId || !body) {
      setLocal(t("chat.guidanceRequired"));
      return;
    }
    setLocal(null);
    setText("");
    void send(body, { type: "add_guidance", task_id: taskId, text: body }, newId(), true);
  };

  const modes: { id: Mode; label: string }[] = [
    { id: "ask", label: t("chat.mode.ask") },
    { id: "task", label: t("chat.mode.task") },
    { id: "guidance", label: t("chat.mode.guidance") },
  ];

  return (
    <section className="card chat-panel" aria-labelledby="chat-title" data-testid="project-chat">
      <div className="card-header">
        <MessageIcon size={18} />
        <h2 id="chat-title" className="card-title">
          {t("overview.chat")}
        </h2>
        <span className="spacer" />
        <span className="subtle" style={{ fontSize: "var(--text-xs)" }}>
          {t("chat.scope")}
        </span>
      </div>
      <div className="segmented chat-modes chat-modes--row" role="group" aria-label={t("chat.modes")}>
        {modes.map((m) => (
          <button key={m.id} type="button" aria-pressed={mode === m.id} onClick={() => setMode(m.id)}>
            {m.label}
          </button>
        ))}
      </div>
      {mode === "ask" ? (
        <AssistantConversation projectId={projectId} compact />
      ) : (
        <>
          <ol className="chat-log" ref={listRef} aria-live="polite" aria-relevant="additions" aria-label={t("chat.log")}>
            {historyState === "loading" ? <li className="subtle chat-note">{t("common.loading")}</li> : null}
            {historyState !== "loading" && entries.length === 0 ? <li className="subtle chat-note">{t("chat.empty")}</li> : null}
            {entries.map((entry) => (
              <ChatEntryView key={entry.id} entry={entry} refLink={(ref) => projectRefLink(ref, projectId, taskLabel, t)} />
            ))}
          </ol>
          <form className="chat-form" onSubmit={submit} noValidate>
            {mode === "task" ? (
              <div className="stack" style={{ gap: 6 }}>
                <TextField label={t("chat.taskTitle")} optionalLabel={t("common.optional")} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} />
                <TextAreaField label={t("chat.criteria")} optionalLabel={t("common.optional")} hint={t("chat.criteriaHint")} value={criteria} onChange={(e) => setCriteria(e.target.value)} rows={2} />
              </div>
            ) : null}
            {mode === "guidance" ? (
              <SelectField label={t("chat.guidanceTask")} value={taskId} onChange={(e) => setTaskId(e.target.value)} hint={t("chat.guidanceHint")}>
                <option value="">{t("chat.chooseTask")}</option>
                {tasks.map((x) => (
                  <option key={x.id} value={x.id}>
                    #{x.number} {x.title}
                  </option>
                ))}
              </SelectField>
            ) : null}
            {local ? (
              <p className="field-error" role="alert">
                {local}
              </p>
            ) : null}
            <div className="chat-composer">
              <label htmlFor="project-chat-input" className="sr-only">
                {mode === "task" ? t("chat.requestLabel") : t("overview.chatInput")}
              </label>
              <textarea
                id="project-chat-input"
                className="input chat-input"
                rows={1}
                placeholder={mode === "task" ? t("chat.requestPlaceholder") : t("overview.chatPlaceholder")}
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={20_000}
              />
              {busy ? (
                <IconButton label={t("chat.stop")} variant="bordered" onClick={() => inflight.current?.abort()} data-testid="chat-stop">
                  <XIcon size={16} />
                </IconButton>
              ) : null}
              <IconButton type="submit" label={t("home.send")} variant="primary" disabled={busy || !text.trim()}>
                <SendIcon size={18} />
              </IconButton>
            </div>
            {busy ? <p className="field-hint">{t("chat.stopHint")}</p> : null}
            <p className="field-hint">{t("chat.commandHint")}</p>
            {historyState === "failed" ? <p className="field-hint">{t("chat.historyUnavailable")}</p> : null}
          </form>
        </>
      )}
    </section>
  );
}
